// frontend/src/pages/JobDetailPage.jsx
import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import NavBar from '../components/NavBar';
import FilterBar from '../components/FilterBar';
import theme from '../theme';

import { useDashboardData } from '../context/DashboardDataContext';
import EntityHeader from '../components/detail/EntityHeader';
import JobTile from '../components/tiles/JobTile';
import LockedScopeBanner from '../components/detail/LockedScopeBanner';
import { unslugify } from '../utils/slug';

import JobSummaries from '../components/detail/JobSummaries';
import RelatedTriggers from '../components/detail/RelatedTriggers';
import RelatedProducts from '../components/detail/RelatedProducts';
import JobInterviewsTable from '../components/detail/tables/JobInterviewsTable';
import RelatedSituations from '../components/detail/RelatedSituations';
import RelatedDesiredOutcomes from '../components/detail/RelatedDesiredOutcomes';
import RelatedMeasures from '../components/detail/RelatedMeasures';
import RelatedInsights from '../components/detail/RelatedInsights';
import RelatedImpressions from '../components/detail/RelatedImpressions';


export default function JobDetailPage() {
  const { jobSlug } = useParams();
  const { ready, getJobSections, jobMaps, jtbdNextGoals, customerStories } = useDashboardData();
  const [showReceipts, setShowReceipts] = React.useState(false);

  // Derive the job name directly from the URL (don’t rely on indices)
  const jobName = React.useMemo(() => unslugify(jobSlug || ''), [jobSlug]);

  // Build sections for this job
  const jobSections = React.useMemo(() => {
    if (!ready || !jobName) return null;
    try {
      const { sections } = getJobSections(jobName);
      console.debug('[JobDetailPage] sections:', (sections || []).map(s => s.title));
      const summarySection =
        (sections || []).find(s => /summary of job/i.test(s.title || '')) || null;
      const jobTile = summarySection?.tiles?.[0] || null;

      // section total may be named differently depending on the builder
      const sectionTotal =
        summarySection?.section_total_interviews ??
        summarySection?.sectionTotal ??
        jobTile?.interviewCount ??
        jobTile?.count ??
        0;

      // Normalize props for <JobTile>
      const headerTileProps = jobTile
        ? {
            title: jobName,
            avgImportance: jobTile.avgImportance,
            avgSatisfaction: jobTile.avgSatisfaction,
            // Summary-of-Job tiles use interviewCount; JobTile expects count
            count: jobTile.count ?? jobTile.interviewCount ?? 0,
            sectionTotal,
            variant: jobTile.variant || 'gap',
            receipts: jobTile.representativeQuotes || jobTile.receipts || []
          }
        : null;

      return { sections, headerTileProps };
    } catch (e) {
      console.warn('[JobDetail] failed to build sections', e);
      return null;
    }
  }, [ready, jobName, getJobSections]);

  if (!ready) return null;

  // If we truly can’t resolve a name, show the message
  if (!jobName) {
    return (
      <ThemeProvider theme={theme}>
        <NavBar />
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Job not found</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Jobs To Be Done
        </Typography>

        <FilterBar />

        {/* Banner showing the locked scope to this job */}
        <LockedScopeBanner value={jobName} />

        {/* Header tile — identical visuals to dashboard */}
        <Box mt={3}>
          <EntityHeader
            title={jobName}
            TileComponent={JobTile}
            tileProps={jobSections?.headerTileProps || {}}
            onBackPath="/dashboard"
            receiptsEnabled={showReceipts}
            onToggleReceipts={() => setShowReceipts((s) => !s)}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Job-only summaries (pulled from getJobSections) */}
        <JobSummaries
          sections={jobSections?.sections || []}
          jobName={jobName}
          jobMaps={jobMaps}
          jtbdNextGoals={jtbdNextGoals}
        />

        {/* Related: Triggers (Top-3 tiles + ranked table) */}
        <RelatedTriggers
          sections={jobSections?.sections || []}
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Related: Products used for this Job */}
        <RelatedProducts
          sections={jobSections?.sections || []}
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Related: Top situations for doing this Job */}
        <RelatedSituations
          sections={jobSections?.sections || []}
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Related: Desired outcomes for doing this Job */}
        <RelatedDesiredOutcomes
          sections={jobSections?.sections || []}
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Related: How do customers measure success? */}
        <RelatedMeasures
          sections={jobSections?.sections || []}
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Related: What 'good' & 'bad' look like (Insights) */}
        <RelatedInsights
          sections={jobSections?.sections || []}
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Related: Positive & Negative Impressions (from Customer Stories) */}
        <RelatedImpressions
          customerStories={customerStories || []}
          jobName={jobName}
        />

        {/* Bottom: Interviews table (object-specific for Job) */}
        <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
          Interviews mentioning this Job
        </Typography>
        <JobInterviewsTable
          jobName={jobName}
          customerStories={customerStories || []}
          variant={(jobSections?.headerTileProps?.variant || 'gap')}
        />
      </Box>
    </ThemeProvider>
  );
}
